/**
 * Hand-drawn vocabulary — containers (cloud, box, bubble, banner) and
 * connectors (arrows) as SVG path data, plus the "marker wobble" filter that
 * makes every stroke read as the same hand.
 *
 * Paths are built from a rect in scene units. All randomness is seeded so a
 * container is stable across re-solves.
 */

import { randRange } from '../lib/tapestryGenerative.js';
import { rectCenter } from './geometry.js';

export const HAND_FILTER_ID = 'scene-hand-wobble';

/** SVG <filter> that displaces strokes with low-frequency noise. */
export function HandFilter({ id = HAND_FILTER_ID, scale = 3.5 }) {
  return (
    <filter id={id} x="-10%" y="-10%" width="120%" height="120%">
      <feTurbulence type="fractalNoise" baseFrequency="0.012" numOctaves="2" seed="7" result="noise" />
      <feDisplacementMap in="SourceGraphic" in2="noise" scale={scale} xChannelSelector="R" yChannelSelector="G" />
    </filter>
  );
}

function fmt(n) {
  return Number(n.toFixed(1));
}

/** A slightly-off rounded rectangle: corners overshoot like a quick marker box. */
export function boxPath(r, rng) {
  const j = () => randRange(rng, -3, 3);
  const { x, y, w, h } = r;
  const k = Math.min(14, w * 0.08, h * 0.2);
  return [
    `M ${fmt(x + k + j())} ${fmt(y + j())}`,
    `L ${fmt(x + w - k + j())} ${fmt(y + j())}`,
    `Q ${fmt(x + w)} ${fmt(y)} ${fmt(x + w + j())} ${fmt(y + k + j())}`,
    `L ${fmt(x + w + j())} ${fmt(y + h - k + j())}`,
    `Q ${fmt(x + w)} ${fmt(y + h)} ${fmt(x + w - k + j())} ${fmt(y + h + j())}`,
    `L ${fmt(x + k + j())} ${fmt(y + h + j())}`,
    `Q ${fmt(x)} ${fmt(y + h)} ${fmt(x + j())} ${fmt(y + h - k + j())}`,
    `L ${fmt(x + j())} ${fmt(y + k + j())}`,
    `Q ${fmt(x)} ${fmt(y)} ${fmt(x + k + j())} ${fmt(y + j())}`,
    'Z',
  ].join(' ');
}

/** Control-point offset of a cloud's scallops (see cloudPath). */
export function cloudBump(r) {
  return Math.max(18, Math.min(r.w, r.h) * 0.22);
}

/**
 * How far a cloud's paint reaches OUTSIDE its rect: a quadratic scallop
 * peaks at half its control offset, and the control sits 0.8–1.2 × bump
 * out, so the curve reaches at most 0.6 × bump. Layout inflates a cloud's
 * rect by this so its scallops don't overlap neighbours.
 */
export function cloudReach(r) {
  return cloudBump(r) * 0.6;
}

/** Cloud: a ring of bumps around the rect. */
export function cloudPath(r, rng) {
  const { x, y, w, h } = r;
  const bump = cloudBump(r);
  const pts = [];
  const nTop = Math.max(3, Math.round(w / (bump * 1.6)));
  const nSide = Math.max(1, Math.round(h / (bump * 1.6)));
  for (let i = 0; i <= nTop; i++) pts.push([x + (w * i) / nTop, y]);
  for (let i = 1; i <= nSide; i++) pts.push([x + w, y + (h * i) / nSide]);
  for (let i = nTop - 1; i >= 0; i--) pts.push([x + (w * i) / nTop, y + h]);
  for (let i = nSide - 1; i >= 1; i--) pts.push([x, y + (h * i) / nSide]);
  let d = `M ${fmt(pts[0][0])} ${fmt(pts[0][1])}`;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const mx = (a[0] + b[0]) / 2;
    const my = (a[1] + b[1]) / 2;
    // Outward normal of the edge (rect is traversed clockwise on screen).
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    const nx = dy / len;
    const ny = -dx / len;
    const s = bump * randRange(rng, 0.8, 1.2);
    d += ` Q ${fmt(mx + nx * s)} ${fmt(my + ny * s)} ${fmt(b[0])} ${fmt(b[1])}`;
  }
  return d + ' Z';
}

/** Speech bubble: box with a tail toward `tail` ([x, y]). */
export function bubblePath(r, rng, tail) {
  const base = boxPath(r, rng).replace(/ Z$/, '');
  if (!tail) return base + ' Z';
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  // Tail reaches at most TAIL_MAX units past the bubble edge toward the target.
  const TAIL_MAX = 70;
  const edge = rectEdgePoint(r, tail);
  const vx = tail[0] - edge[0];
  const vy = tail[1] - edge[1];
  const vl = Math.hypot(vx, vy) || 1;
  const reach = Math.min(TAIL_MAX, vl);
  const tx = edge[0] + (vx / vl) * reach;
  const ty = edge[1] + (vy / vl) * reach;
  // Tail leaves from the edge nearest the target.
  const dx = tx - cx;
  const dy = ty - cy;
  let ax;
  let ay;
  let bx;
  let by;
  const t = 12;
  if (Math.abs(dx) * r.h > Math.abs(dy) * r.w) {
    ax = bx = dx > 0 ? r.x + r.w : r.x;
    ay = cy - t;
    by = cy + t;
  } else {
    ay = by = dy > 0 ? r.y + r.h : r.y;
    ax = cx - t;
    bx = cx + t;
  }
  return `${base} Z M ${fmt(ax)} ${fmt(ay)} L ${fmt(tx)} ${fmt(ty)} L ${fmt(bx)} ${fmt(by)}`;
}

/** Depth of a banner's end notches (they eat into the rect, so pad for them). */
export function bannerNotch(h) {
  return Math.min(h * 0.4, 22);
}

/** Banner: rect with notched ends. */
export function bannerPath(r, rng) {
  const j = () => randRange(rng, -2, 2);
  const { x, y, w, h } = r;
  const n = bannerNotch(h);
  return [
    `M ${fmt(x + j())} ${fmt(y + j())}`,
    `L ${fmt(x + w + j())} ${fmt(y + j())}`,
    `L ${fmt(x + w - n + j())} ${fmt(y + h / 2)}`,
    `L ${fmt(x + w + j())} ${fmt(y + h + j())}`,
    `L ${fmt(x + j())} ${fmt(y + h + j())}`,
    `L ${fmt(x + n + j())} ${fmt(y + h / 2)}`,
    'Z',
  ].join(' ');
}

/** Underline: a single stroke under the rect's bottom edge. */
export function underlinePath(r, rng) {
  const y = r.y + r.h + 6;
  return `M ${fmt(r.x)} ${fmt(y + randRange(rng, -2, 2))} Q ${fmt(r.x + r.w / 2)} ${fmt(y + randRange(rng, -6, 6))} ${fmt(
    r.x + r.w
  )} ${fmt(y + randRange(rng, -2, 2))}`;
}

export function containerPath(kind, rect, rng, extra) {
  switch (kind) {
    case 'box':
      return boxPath(rect, rng);
    case 'cloud':
      return cloudPath(rect, rng);
    case 'bubble':
      return bubblePath(rect, rng, extra?.tail);
    case 'banner':
      return bannerPath(rect, rng);
    case 'underline':
      return underlinePath(rect, rng);
    default:
      return null;
  }
}

/** Padding a container needs around its content, per kind. */
export function containerPadding(kind, fontSize) {
  switch (kind) {
    case 'cloud':
      return fontSize * 0.9;
    case 'bubble':
    case 'box':
    case 'banner':
      return fontSize * 0.55;
    default:
      return fontSize * 0.2;
  }
}

/**
 * Point on a rect's boundary in the direction of `toward` from its center.
 */
export function rectEdgePoint(r, toward) {
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  const dx = toward[0] - cx;
  const dy = toward[1] - cy;
  if (!dx && !dy) return [cx, cy];
  const sx = Math.abs(dx) > 1e-6 ? r.w / 2 / Math.abs(dx) : Infinity;
  const sy = Math.abs(dy) > 1e-6 ? r.h / 2 / Math.abs(dy) : Infinity;
  const s = Math.min(sx, sy);
  return [cx + dx * s, cy + dy * s];
}

/** Curved arrow from rect A to rect B; returns {d, head}. */
export function connectorPath(from, to, rng, { gap = 14, curve = 0.18 } = {}) {
  const a = rectEdgePoint(from, rectCenter(to));
  const b = rectEdgePoint(to, rectCenter(from));
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  // Trim both ends off the boxes.
  const sx = a[0] + ux * gap;
  const sy = a[1] + uy * gap;
  const ex = b[0] - ux * gap;
  const ey = b[1] - uy * gap;
  // Control point offset to one side for a lazy arc.
  const side = rng() < 0.5 ? -1 : 1;
  const cx = (sx + ex) / 2 - uy * len * curve * side;
  const cy = (sy + ey) / 2 + ux * len * curve * side;
  // Arrowhead along the end tangent (from ctrl to end).
  const tdx = ex - cx;
  const tdy = ey - cy;
  const tl = Math.hypot(tdx, tdy) || 1;
  const tx = tdx / tl;
  const ty = tdy / tl;
  const hs = 16;
  const head = `M ${fmt(ex - tx * hs - ty * hs * 0.55)} ${fmt(ey - ty * hs + tx * hs * 0.55)} L ${fmt(ex)} ${fmt(
    ey
  )} L ${fmt(ex - tx * hs + ty * hs * 0.55)} ${fmt(ey - ty * hs - tx * hs * 0.55)}`;
  return { d: `M ${fmt(sx)} ${fmt(sy)} Q ${fmt(cx)} ${fmt(cy)} ${fmt(ex)} ${fmt(ey)}`, head, mid: [cx, cy] };
}
